import { SUCCESS_SEGMENT } from "@/enums/route";

/**
 * The steps of a routed money flow (pay, exchange). Numbered to match the
 * progress bar's `currentStep`; `None` lights no step.
 */
export enum FlowStep {
  None = 0,
  Amount = 1,
  Review = 2,
  Success = 3,
}

/**
 * Reads the step from a fixed position of the flow layout's own segments
 * (`useSelectedLayoutSegments()` called in that layout), never from a
 * pathname substring: ids are opaque and may contain any step word.
 *
 * - `[]` → amount (the layout's own page)
 * - `[id]` → review of quote `id`
 * - `[id, "success"]` → success
 *
 * See `.claude/rules/web/step-flows.md`.
 */
export function getFlowStep(segments: readonly string[]): FlowStep {
  switch (segments.length) {
    case 0:
      return FlowStep.Amount;
    case 1:
      return FlowStep.Review;
    case 2:
      return segments[1] === SUCCESS_SEGMENT ? FlowStep.Success : FlowStep.None;
    default:
      return FlowStep.None;
  }
}

/**
 * The quote id while the flow is on its review step, else undefined. The id
 * on the success step names a booked trade, not a live quote, so it is never
 * returned there. The value is in the router's (percent-encoded) form; pass it
 * through `decodeRouteParam` before handing it to a route builder.
 */
export function getReviewQuoteId(
  segments: readonly string[]
): string | undefined {
  return getFlowStep(segments) === FlowStep.Review ? segments[0] : undefined;
}
